import { daysDifference } from '@/_utils/days-difference';
import { components } from '@/common/bag-api';
import { Data2Service } from '@/common/data2.service';
import { DateService } from '@/common/date.service';
import { PriceAdjustmentReservations } from '@/common/enums/PriceAdjustmentReservations';
import { TransportKind } from '@/common/enums/TransportKind';
import { Seller } from '@/common/enums/Seller';
import { FiltersComponent } from '@/common/filters.component';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { LoaderService } from '@/common/loader/loader.service';
import { DatePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, input, Input, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
	CancellationStatusesListReservation,
	CommonStatusesListReservation,
	RejectedStatusesListReservation,
	Status,
} from 'src/app/common/enums/Status';
import {
	BagReservationListFilters,
	FilterDefinition,
	OptionWithShortName,
	ParseFilters,
	SelectOption,
	UserOption,
} from '../bag-reservation-list.model';
import {
	DEFAULT_BAG_RESERVATION_FILTERS,
	DEFAULT_PARSE_FILTERS,
	FILTERS_DEFINITION,
} from '../bag-reservation-list-config';

type RoomType = components['schemas']['RoomType'];

@Component({
	selector: 'app-bag-reservation-list-filter',
	templateUrl: './bag-reservation-list-filter.component.html',
	styleUrls: ['./bag-reservation-list-filter.component.scss'],
})
export class BagReservationListFilterComponent extends FiltersComponent implements AfterViewInit {
	@Input() products: SelectOption[];
	@Input() destinations: SelectOption[];
	@Input() locations: SelectOption[];
	@Input() transportStops: string[];
	@Input() productKinds: SelectOption[];
	@Input() roomTypes: RoomType[];
	@Input() alerts: SelectOption[];
	@Input() status: SelectOption[];
	@Input() organizations: OptionWithShortName[];
	@Input() channels: SelectOption[];
	@Input() users: UserOption[];
	@Input() services: SelectOption[];
	@Input() agencies: OptionWithShortName[];
	@Input() paymentStatus: SelectOption[];
	@Input() seasons: SelectOption[];
	@Output() displayStat = new EventEmitter();

	readonly paymentKinds = input.required<SelectOption[]>();

	showStatistic = {
		filters: null,
		show: false,
		allFilters: null,
	};

	parseFilters: ParseFilters = DEFAULT_PARSE_FILTERS;

	filters: BagReservationListFilters = DEFAULT_BAG_RESERVATION_FILTERS;

	readonly transportTypesOptions: { code: TransportKind; label: string }[] = [
		{ code: TransportKind.COACH, label: 'Autobus' },
		{ code: TransportKind.FLIGHT, label: 'Samolot' },
		{ code: TransportKind.WITHOUT, label: 'Dojazd własny' },
		{ code: TransportKind.MIX, label: 'Mieszany' },
	];

	dateFilterEnd: string[] = [
		'time_lower_before',
		'time_upper_before',
		'reservation_time__lte',
		'confirmation_time__lte',
	];

	dateFilterBegin: string[] = [
		'time_lower_after',
		'time_upper_after',
		'reservation_time__gte',
		'confirmation_time__gte',
	];

	protected readonly Seller = Seller;
	protected readonly PriceAdjustmentReservations = PriceAdjustmentReservations;

	protected filtersDefinition: FilterDefinition = FILTERS_DEFINITION;

	constructor(
		protected api: Data2Service,
		protected route: ActivatedRoute,
		protected router: Router,
		protected date: DateService,
		protected loader: LoaderService,
		protected infobox: InfoboxService,
		private readonly datePipe: DatePipe,
		private readonly cdr: ChangeDetectorRef,
	) {
		super(api, route, router);
	}

	ngAfterViewInit(): void {
		this.checkQueryParamsAndReturnSevenDaysBeforeDate().subscribe(async (date) => {
			if (!this.parseFilters.reservation_time__gte) {
				this.parseFilters.reservation_time__gte = this.datePipe.transform(date, 'YYYY-MM-dd');
				const shouldLoadFilters = await this.filtersSave();
				if (shouldLoadFilters) {
					this.filtersLoad();
				}
			}
		});

		this.cdr.detectChanges();
	}

	checkStatusFilter(): void {
		this.setFilterStatus('status');
		this.setFilterStatus('offer__status');
		this.filters2url(this.filters);
	}

	onPaymentStatusChange(): void {
		const paymentLength = this.filters.payment_status.length;
		this.filters.payment_status = paymentLength > 0 ? [this.filters.payment_status[paymentLength - 1]] : [];
	}

	get statsDisabled(): boolean {
		const ONE_MONTH = 31;

		const datePairs = [
			[this.parseFilters.reservation_time__gte, this.parseFilters.reservation_time__lte],
			[this.parseFilters.confirmation_time__gte, this.parseFilters.confirmation_time__lte],
			[this.parseFilters.time_lower_after, this.parseFilters.time_lower_before],
		];

		const isWithinLimit = datePairs.some(([start, end]) => {
			const diff = daysDifference(new Date(start), new Date(end));
			return typeof diff === 'number' && Math.abs(diff) <= ONE_MONTH;
		});

		if (isWithinLimit) {
			return false;
		}

		this.showStatistic.show = false;
		return true;
	}

	async filtersSave(): Promise<boolean> {
		this.convertSaveFilters();
		this.filtersClose();
		return new Promise((resolve, reject) => {
			this.api.put('/userFilters/' + this.userFilters, this.filters, (result, error) => {
				if (error) {
					reject(false);
				}
				resolve(true);
			});
		});
	}

	filtersClear(): void {
		Object.keys(this.filters).forEach((key) => {
			if (Array.isArray(this.filters[key])) {
				this.filters[key] = [];
			}
			this.filters[key] = null;
		});
		Object.keys(this.parseFilters).forEach((key) => {
			if (Array.isArray(this.parseFilters[key])) {
				this.parseFilters[key] = [];
			}
			this.parseFilters[key] = null;
		});
	}

	filtersClose(): void {
		this.showStatistic.filters = this.parseFilters.status?.filter((obj: Status) => {
			CommonStatusesListReservation.includes(obj);
		});
		this.showStatistic.allFilters = this.filters;
		return this.displayStat.emit(this.showStatistic);
	}

	protected filters2url(filters: BagReservationListFilters): void {
		if (!filters || filters instanceof Array) {
			this.selected.emit();
			return;
		}
		filters.on = 1;
		this.router.navigate(['.'], {
			relativeTo: this.route,
			queryParams: filters,
			preserveFragment: true,
		});
	}

	protected convertSaveFilters(): void {
		Object.keys(this.filters).forEach((key) => {
			if (this.filters[key] === '' && !this.filters[key]) this.filters[key] = null;
			if (!Array.isArray(this.filters[key]) && this.parseFilters[key])
				this.filters[key] = (this.parseFilters[key] * 100).toFixed(0);
			if (this.dateFilterEnd.includes(key) && this.parseFilters[key]) {
				this.filters[key] = `${this.parseFilters[key]}T23:59`;
			}
			if (this.dateFilterBegin.includes(key) && this.parseFilters[key]) {
				this.filters[key] = `${this.parseFilters[key]}T00:00`;
			}
		});
		Object.keys(this.parseFilters).forEach((key) => {
			if (!this.parseFilters[key]) this.filters[key] = null;
		});
		this.checkStatusFilter();
	}

	protected filtersAfterLoad(): void {
		Object.keys(this.parseFilters).forEach((key) => {
			if (!Array.isArray(this.parseFilters[key]) && this.filters[key])
				this.parseFilters[key] = (this.filters[key] / 100).toFixed(2);
			if (this.dateFilterEnd.includes(key) && this.filters[key])
				this.parseFilters[key] = this.date.toUtcDate(this.filters[key]);
			if (this.dateFilterBegin.includes(key) && this.filters[key])
				this.parseFilters[key] = this.date.toUtcDate(this.filters[key]);
		});

		this.setFilterStatusAfterLoad('status');
		this.setFilterStatusAfterLoad('offer__status');
	}

	private setFilterStatusAfterLoad(filterName: string) {
		this.parseFilters[filterName] = [];
		this.filters[filterName]?.forEach((obj) => {
			switch (obj) {
				case Status.CANCELED:
					this.parseFilters[filterName].push(Status.CANCELED);
					break;
				case Status.REJECTED_EXPIRATION:
					this.parseFilters[filterName].push(Status.REJECTED);
					break;
				case Status.OPTION:
					this.parseFilters[filterName].push(Status.OPTION);
					break;
				case Status.CONFIRMED:
					this.parseFilters[filterName].push(Status.CONFIRMED);
					break;
			}
		});
	}

	private setFilterStatus(filterName: string) {
		this.filters[filterName] = [];
		this.parseFilters[filterName]?.forEach((obj) => {
			switch (obj) {
				case Status.CANCELED:
					this.filters[filterName].push(...CancellationStatusesListReservation);
					break;
				case Status.REJECTED:
					this.filters[filterName].push(...RejectedStatusesListReservation);
					break;
				case Status.OPTION:
					this.filters[filterName].push(Status.OPTION);
					break;
				case Status.CONFIRMED:
					this.filters[filterName].push(Status.CONFIRMED);
					break;
				default:
					this.filters[filterName] = [];
					break;
			}
		});
	}
}
